/* ============================================
   pm-filters.js — hybrid filter controller

   Architecture (validated against Sephora, NAP, ASOS, Wayfair, FragranceX,
   Selfridges patterns 2026-05-14):

   1. Liquid renders facet checkboxes (data-facet + data-value) and product
      cards with data-facet-* attributes containing the card's tag values.
   2. On checkbox change, JS filters cards in DOM instantly (no page reload).
   3. JS updates URL via History API in Shopify-native `?filter.p.tag=*`
      pattern. URL is shareable + restorable on reload.
   4. When Shopify Admin → Search & Discovery is configured for those tag
      filters, the SAME URLs are server-routed too, so direct-link arrivals
      see filtered content from the server (best for SEO + AI Overviews).
   5. Until admin is configured, JS handles filtering entirely; URL is
      cosmetic but harmless (also see canonical + noindex in theme.liquid
      and robots.txt allow-list for any URL Google sees).

   Native Shopify filters (Availability, Price, anything admin-configured)
   still work via data-pmf-checkbox path (form-submit → server filter).
   ============================================ */

(function () {
  'use strict';

  const root = document.querySelector('[data-pmf]');
  if (!root) return;

  const sidebar = root.querySelector('[data-pmf-sidebar]');
  const trigger = document.getElementById('pmfMobileTrigger');
  const form = root.querySelector('[data-pmf-form]');
  const sortSelect = root.querySelector('[data-pmf-sort]');
  const closeButtons = root.querySelectorAll('[data-pmf-close]');

  const cards = root.querySelectorAll('[data-pmf-card]');
  const facetCheckboxes = root.querySelectorAll('[data-pmf-facet]');
  const countEl = root.querySelector('[data-pmf-count]') || root.querySelector('.pmf__count');
  const pillsEl = root.querySelector('.pmf__pills');
  const gridEl = root.querySelector('.pmf__grid');
  const toolbarRight = root.querySelector('.pmf__toolbar-right');

  // ===== Mobile drawer open/close =====
  let lastFocus = null;
  function openDrawer() {
    if (!sidebar) return;
    lastFocus = document.activeElement;
    sidebar.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('pmf-drawer-open');
    setTimeout(function () {
      const closeBtn = sidebar.querySelector('.pmf__sidebar-close');
      if (closeBtn) closeBtn.focus();
    }, 240);
  }
  function closeDrawer() {
    if (!sidebar) return;
    sidebar.setAttribute('aria-hidden', 'true');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('pmf-drawer-open');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }
  if (trigger && sidebar) {
    trigger.addEventListener('click', function () {
      sidebar.getAttribute('aria-hidden') === 'false' ? closeDrawer() : openDrawer();
    });
  }
  closeButtons.forEach(function (btn) { btn.addEventListener('click', closeDrawer); });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sidebar && sidebar.getAttribute('aria-hidden') === 'false') closeDrawer();
  });

  // ===== State: read active facets from URL =====
  // URL pattern: ?filter.p.tag=note:oud&filter.p.tag=family:floral
  // We accept that or our internal pattern (?pmf_note=oud,leather&pmf_family=floral)
  function readActiveFiltersFromUrl() {
    const state = { concentration: new Set(), family: new Set(), note: new Set(), gender: new Set(), sale: new Set() };
    const params = new URLSearchParams(window.location.search);
    params.getAll('filter.p.tag').forEach(function (raw) {
      const v = decodeURIComponent(raw);
      if (v.indexOf('concentration:') === 0) state.concentration.add(v.slice(14));
      else if (v.indexOf('family:') === 0) state.family.add(v.slice(7));
      else if (v.indexOf('note:') === 0) state.note.add(v.slice(5));
      else if (v === 'men' || v === 'women-perfumes' || v === 'unisex-perfumes') state.gender.add(v);
      else if (v === 'on-sale') state.sale.add('1');
    });
    return state;
  }

  // ===== Apply state to checkboxes (restore on load / popstate) =====
  function syncCheckboxesFromState(state) {
    facetCheckboxes.forEach(function (cb) {
      const facet = cb.dataset.facet;
      const value = cb.dataset.value;
      cb.checked = state[facet] && state[facet].has(value);
    });
  }

  // ===== Filter cards in DOM based on state =====
  function applyFilter(state) {
    let visible = 0;
    cards.forEach(function (card) {
      let show = true;
      // Each facet group is AND'd; values within a group are OR'd
      ['concentration', 'family', 'note', 'gender'].forEach(function (facet) {
        if (!show) return;
        const selected = state[facet];
        if (!selected || selected.size === 0) return;
        const cardAttr = card.getAttribute('data-facet-' + facet) || '';
        // cardAttr format: "|val1|val2|val3|" — match by checking pipe-wrapped value
        let anyMatch = false;
        selected.forEach(function (v) {
          if (cardAttr.indexOf('|' + v + '|') !== -1) anyMatch = true;
        });
        if (!anyMatch) show = false;
      });
      if (show && state.sale && state.sale.size > 0) {
        if (card.getAttribute('data-facet-sale') !== '1') show = false;
      }
      card.style.display = show ? '' : 'none';
      if (show) visible++;
    });
    // Update count
    if (countEl) {
      countEl.firstChild && (countEl.firstChild.textContent = visible + ' ');
      const labelSpan = countEl.querySelector('.pmf__count-label');
      if (labelSpan) labelSpan.textContent = visible === 1 ? 'product' : 'products';
    }
    // Toggle empty state
    let emptyEl = root.querySelector('.pmf__empty-js');
    if (visible === 0) {
      if (!emptyEl) {
        emptyEl = document.createElement('div');
        emptyEl.className = 'pmf__empty pmf__empty-js';
        emptyEl.innerHTML = '<p class="pmf__empty-title">No products match these filters</p>' +
                            '<p class="pmf__empty-sub">Try removing a filter, or <a href="' + window.location.pathname + '">browse the full collection</a>.</p>';
        if (gridEl && gridEl.parentNode) gridEl.parentNode.insertBefore(emptyEl, gridEl);
      }
      if (gridEl) gridEl.style.display = 'none';
    } else {
      if (emptyEl) emptyEl.remove();
      if (gridEl) gridEl.style.display = '';
    }
    // Trigger button label update
    let activeCount = 0;
    ['concentration', 'family', 'note', 'gender'].forEach(function (f) {
      if (state[f]) activeCount += state[f].size;
    });
    if (state.sale && state.sale.size > 0) activeCount += 1;
    if (trigger) {
      const baseLabel = 'Filter';
      trigger.lastChild && (trigger.lastChild.textContent = activeCount ? (' ' + baseLabel + ' (' + activeCount + ')') : (' ' + baseLabel));
    }

    renderPills(state);
  }

  // ===== Render active filter pills =====
  function renderPills(state) {
    if (!pillsEl) return;
    // Remove only pills we own (data-pmf-pill); keep server-rendered ones
    pillsEl.querySelectorAll('[data-pmf-pill]').forEach(function (el) { el.remove(); });
    const facetLabels = { concentration: '', family: '', note: '', gender: '', sale: 'Sale' };
    function valueLabel(facet, value) {
      if (facet === 'gender') {
        if (value === 'men') return 'Men';
        if (value === 'women-perfumes') return 'Women';
        if (value === 'unisex-perfumes') return 'Unisex';
      }
      if (facet === 'sale') return 'On sale';
      // Concentration nice labels
      if (facet === 'concentration') {
        if (value === 'eau-de-parfum') return 'Eau de Parfum';
        if (value === 'eau-de-toilette') return 'Eau de Toilette';
        if (value === 'extrait-de-parfum') return 'Extrait de Parfum';
        if (value === 'eau-fraiche') return 'Eau Fraîche';
      }
      return value.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    }
    Object.keys(state).forEach(function (facet) {
      state[facet].forEach(function (value) {
        const a = document.createElement('a');
        a.href = '#';
        a.className = 'pmf__pill';
        a.setAttribute('data-pmf-pill', '');
        a.setAttribute('data-facet', facet);
        a.setAttribute('data-value', value);
        a.setAttribute('rel', 'nofollow');
        a.innerHTML = '<span class="pmf__pill-label">' + valueLabel(facet, value) + '</span><span class="pmf__pill-x" aria-hidden="true">×</span>';
        a.addEventListener('click', function (e) {
          e.preventDefault();
          state[facet].delete(value);
          const cb = root.querySelector('[data-pmf-facet][data-facet="' + facet + '"][data-value="' + value + '"]');
          if (cb) cb.checked = false;
          applyFilter(state);
          updateUrl(state);
        });
        pillsEl.appendChild(a);
      });
    });
  }

  // ===== Update URL via History API (shareable + server-routable once admin enables) =====
  function updateUrl(state) {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    params.delete('filter.p.tag');
    params.delete('page');
    // Reconstruct filter.p.tag entries
    ['concentration', 'family', 'note'].forEach(function (facet) {
      state[facet].forEach(function (v) { params.append('filter.p.tag', facet + ':' + v); });
    });
    state.gender.forEach(function (v) { params.append('filter.p.tag', v); });
    if (state.sale.size > 0) params.append('filter.p.tag', 'on-sale');
    window.history.replaceState({}, '', url.toString());
  }

  // ===== Wire up checkbox changes =====
  let currentState = readActiveFiltersFromUrl();
  syncCheckboxesFromState(currentState);
  applyFilter(currentState);

  facetCheckboxes.forEach(function (cb) {
    cb.addEventListener('change', function () {
      const facet = cb.dataset.facet;
      const value = cb.dataset.value;
      if (!currentState[facet]) currentState[facet] = new Set();
      if (cb.checked) currentState[facet].add(value);
      else currentState[facet].delete(value);
      applyFilter(currentState);
      updateUrl(currentState);
    });
  });

  // popstate (back/forward navigation)
  window.addEventListener('popstate', function () {
    currentState = readActiveFiltersFromUrl();
    syncCheckboxesFromState(currentState);
    applyFilter(currentState);
  });

  // Native Shopify filter (Availability, Price) — full page nav via Shopify URL
  if (form) {
    form.addEventListener('change', function (event) {
      const cb = event.target;
      if (!cb.matches('input[type=checkbox]')) return;
      if (cb.matches('[data-pmf-checkbox]')) {
        const target = cb.checked ? cb.dataset.urlAdd : cb.dataset.urlRemove;
        if (target && !isMobile()) window.location.href = target;
        return;
      }
    });
    let priceTimer = null;
    form.addEventListener('input', function (event) {
      if (!event.target.matches('[data-pmf-price-min], [data-pmf-price-max]')) return;
      if (isMobile()) return;
      clearTimeout(priceTimer);
      priceTimer = setTimeout(function () { form.submit(); }, 700);
    });
    form.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      if (!event.target.matches('[data-pmf-price-min], [data-pmf-price-max]')) return;
      event.preventDefault();
      form.submit();
    });
  }

  function isMobile() { return window.matchMedia('(max-width: 989px)').matches; }

  // Sort dropdown
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  // Mobile focus trap
  if (sidebar) {
    sidebar.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (window.innerWidth > 989) return;
      const focusables = sidebar.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, summary');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }
})();
